import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import './Login.css';

const RegisterParent = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setAuth } = useAuth();
  
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    registration_code: ''
  });
  
  const [subdomain, setSubdomain] = useState('');
  const [districtName, setDistrictName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [codeValidated, setCodeValidated] = useState(false);

  useEffect(() => {
    // Extract subdomain from hostname or ?subdomain= query param
    const hostname = window.location.hostname;
    const parts = hostname.split('.');
    const querySubdomain = searchParams.get('subdomain') || '';
    let detectedSubdomain = '';

    if (hostname.includes('localhost')) {
      detectedSubdomain = querySubdomain;
    } else if (parts.length >= 4 && parts[0] !== 'www' && parts[0] !== 'smadmin') {
      // District subdomain: smartmarketing.backtoschool.smdemo.website
      detectedSubdomain = parts[0];
    } else if (querySubdomain) {
      // Main domain with ?subdomain= param: backtoschool.smdemo.website/register?subdomain=smartmarketing
      detectedSubdomain = querySubdomain;
    } else if (parts.length >= 3 && parts[0] !== 'www' && parts[0] !== 'smadmin') {
      detectedSubdomain = parts[0];
    }

    setSubdomain(detectedSubdomain);
  }, [searchParams]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleValidateCode = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.registration_code || !subdomain) {
      setError('Please enter a registration code');
      return;
    }

    try {
      const response = await axios.post('/api/registration-codes/validate', {
        code: formData.registration_code,
        subdomain: subdomain
      });

      if (response.data.valid) {
        setCodeValidated(true);
        setDistrictName(response.data.district_name);
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Invalid registration code');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post('/api/auth/register-parent', {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        first_name: formData.first_name,
        last_name: formData.last_name,
        registration_code: formData.registration_code,
        subdomain: subdomain
      });

      // Handle both { token, user } and { data: { token, user } } response shapes
      const payload = response.data?.data || response.data;
      const token = payload?.token;
      const user = payload?.user;

      if (!token || !user?.role) {
        setError('Invalid server response. Please try again.');
        return;
      }

      // Auto-login after registration - update AuthContext so user/role are available
      setAuth(token, user);

      // Redirect to parent dashboard
      navigate('/parent');
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  if (!subdomain) {
    return (
      <div className="login-container">
        <div className="login-box">
          <h1>Back to School Forms</h1>
          <h2>Registration</h2>
          <div className="error-message">
            Unable to detect district. Please visit your district's registration page.
          </div>
        </div>
      </div>
    );
  }

  if (!codeValidated) {
    return (
      <div className="login-container">
        <div className="login-box">
          <h1>Back to School Forms</h1>
          <h2>Parent Registration</h2>
          <p style={{ marginBottom: '1.5rem', color: '#666', textAlign: 'center' }}>
            Enter your registration code to continue
          </p>
          {error && <div className="error-message">{error}</div>}
          <form onSubmit={handleValidateCode}>
            <div className="form-group">
              <label>Registration Code *</label>
              <input
                type="text"
                name="registration_code"
                value={formData.registration_code}
                onChange={handleChange}
                required
                placeholder="Enter your registration code"
                autoFocus
              />
            </div>
            <button type="submit" className="btn-primary">Continue</button>
          </form>
          <div style={{ marginTop: '1rem', textAlign: 'center' }}>
            <a href="/login" style={{ color: '#667eea', textDecoration: 'none', fontSize: '0.9rem' }}>
              Already have an account? Login
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-box" style={{ maxWidth: '500px' }}>
        <h1>Back to School Forms</h1>
        <h2>Create Parent Account</h2>
        <p style={{ marginBottom: '1.5rem', color: '#666', textAlign: 'center' }}>
          Registering for: <strong>{districtName}</strong>
        </p>
        {error && <div className="error-message">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div className="form-group">
              <label>First Name</label>
              <input
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Last Name</label>
              <input
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-group">
            <label>Username *</label>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Email Address *</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Password *</label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              minLength={8}
            />
            <small style={{ display: 'block', marginTop: '0.25rem', color: '#666', fontSize: '0.875rem' }}>
              Must be at least 8 characters
            </small>
          </div>

          <div className="form-group">
            <label>Confirm Password *</label>
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              minLength={8}
            />
          </div>

          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>
        <div style={{ marginTop: '1rem', textAlign: 'center' }}>
          <a href="/login" style={{ color: '#667eea', textDecoration: 'none', fontSize: '0.9rem' }}>
            Already have an account? Login
          </a>
        </div>
      </div>
    </div>
  );
};

export default RegisterParent;




